/* PALLMILLA motion layer — the ONE motion system.
 *
 * Self-hosted. p-motion-lib.js is motion@13.4.6 (the hybrid browser build,
 * pinned; verified as the current latest on the npm registry). It is loaded
 * from our own asset URL, never hotlinked from a CDN at runtime, so there is
 * no third-party DNS/TLS hop in the critical path — the same decision already
 * made for the self-hosted fonts.
 *
 * SPEC: transform strings + opacity ONLY. Never height, top or margin — those
 * trigger layout and are the classic cause of jank on low-end Android.
 *
 * Motion's own documentation states that the x / y / scale / rotate shorthands
 * are driven through CSS variables and are therefore NOT hardware-accelerated.
 * That is why every call below animates a literal transform string instead.
 *
 * SAFETY CONTRACT (the one p-reveal.js held, kept on purpose):
 *   1. Reduced motion bails FIRST, before anything is touched.
 *   2. No library, or no IntersectionObserver, bails before anything is hidden.
 *   3. NOTHING in CSS hides content. The hidden state is applied here, in JS,
 *      and only after both gates above have passed.
 *   4. Everything runs inside try/catch. On any failure every element this file
 *      hid is put back. A decorative animation must never be able to take
 *      content off the page.
 *   5. Once an animation finishes, its inline styles are cleared so the element
 *      returns to its natural CSS state. Without this the inline
 *      transform: translateY(0px) would out-rank the hover lift on cards.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------- 1. the gates */
  var reduce = false;
  try {
    reduce = !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) { reduce = false; }
  if (reduce) return;

  var M = window.Motion;
  if (!M || typeof M.animate !== 'function') return;
  if (!('IntersectionObserver' in window)) return;

  var EASE = [0.22, 1, 0.36, 1];        /* == --p-ease-out */
  var HIDDEN = 'translateY(28px)';

  /* Every element we have hidden, so a failure can put all of them back. */
  var armed = [];
  function hide(el) {
    armed.push(el);
    el.style.opacity = '0';
    el.style.transform = HIDDEN;
  }
  function release(el) {
    el.style.opacity = '';
    el.style.transform = '';
  }
  function revealAll() {
    for (var i = 0; i < armed.length; i++) {
      try { release(armed[i]); } catch (e) {}
    }
  }
  /* Hand the element back to CSS the moment the animation lands. */
  function handBack(anim, el) {
    var done = anim && anim.finished;
    if (done && typeof done.then === 'function') {
      done.then(function () { release(el); }, function () { release(el); });
    } else {
      release(el);
    }
  }
  function list(nodes) { return Array.prototype.slice.call(nodes); }

  try {
    /* ------------------------------------------------------- 2. the reveal */
    /* y + opacity, 0.7s on the expressive ease, fired once when the element is
       15% in and 10% up from the bottom of the viewport. */
    var REVEAL = '.p-about__grid, .p-band--paper .p-head, ' +
      '.p-band--sand .p-head, .p-band--deep .p-head, .p-cta, .p-prod .p-rv';

    var reveal = list(document.querySelectorAll(REVEAL));
    var i;
    for (i = 0; i < reveal.length; i++) hide(reveal[i]);

    if (reveal.length) {
      M.inView(reveal, function (el) {
        handBack(M.animate(el,
          { opacity: 1, transform: 'translateY(0px)' },
          { duration: 0.7, ease: EASE }), el);
      }, { margin: '0px 0px -10% 0px', amount: 0.15 });
    }

    /* --------------------------------------------------- 3. grid cascade */
    /* Cards rise in sequence from the centre outwards rather than popping in
       as one block. */
    var grids = list(document.querySelectorAll('.p-grid'));
    for (i = 0; i < grids.length; i++) {
      (function (grid) {
        var kids = list(grid.children);
        if (!kids.length) return;
        var k;
        for (k = 0; k < kids.length; k++) hide(kids[k]);
        M.inView(grid, function () {
          var anim = M.animate(kids,
            { opacity: 1, transform: 'translateY(0px)' },
            { duration: 0.65, ease: EASE,
              delay: M.stagger(0.06, { from: 'center' }) });
          var n;
          for (n = 0; n < kids.length; n++) handBack(anim, kids[n]);
        }, { margin: '0px 0px -10% 0px', amount: 0.15 });
      })(grids[i]);
    }

    /* ---------------------------------------------------- 4. hero parallax */
    /* Only the IMAGE moves, and it moves at a fixed 1.12 scale so it can never
       slide far enough to expose the wash behind it. The SECTION is left alone:
       it already carries the CSS p-settle entrance, and a CSS animation
       out-ranks an inline transform, so animating it here would silently do
       nothing at all.
       Offset range start->start / end->start: the hero is the FIRST section, so
       it is already part-way through any "entering" range at load. This range
       runs exactly while the hero scrolls out, which is the range a viewer
       actually sees. */
    var heroImg = document.querySelector('.p-hero__img');
    if (heroImg && typeof M.scroll === 'function') {
      M.scroll(
        M.animate(heroImg,
          { transform: ['translateY(-3%) scale(1.12)', 'translateY(3%) scale(1.12)'] },
          { ease: 'linear' }),
        { target: heroImg, offset: ['start start', 'end start'] }
      );
    }

    /* ------------------------------------------------ 5. PDP crossfade */
    /* 220ms, opacity only. Starts at 0.2 and never at 0: if the tab is
       backgrounded mid-animation the fallback path pauses, and an element
       parked at opacity 0 would be an invisible product photo. */
    var pdp = document.querySelector('.p-product-main');
    if (pdp) {
      pdp.addEventListener('click', function (ev) {
        try {
          var hit = ev.target.closest && ev.target.closest('[data-media-id]');
          if (!hit) return;
          var id = hit.getAttribute('data-media-id');
          if (!id) return;
          var item = pdp.querySelector('.product__media-item[data-media-id="' + id + '"]') ||
                     pdp.querySelector('[data-media-id="' + id + '"]');
          if (!item || item === hit || item.contains(hit)) return;
          var img = item.querySelector('img') || item;
          M.animate(img, { opacity: [0.2, 1] }, { duration: 0.22, ease: 'linear' });
        } catch (e) {}
      });
    }

    /* ------------------------------------------------------- 6. the spring */
    /* Sticky mobile add-to-bag, stiffness 300. NO SUCH ELEMENT EXISTS in this
       theme yet, so this is a live hook rather than a live behaviour: mark the
       bar data-p-spring and it springs in once, on first view, with no further
       change needed here. It is written now so the bar cannot arrive without
       it. */
    var springs = list(document.querySelectorAll('[data-p-spring]'));
    for (i = 0; i < springs.length; i++) hide(springs[i]);
    if (springs.length) {
      M.inView(springs, function (el) {
        handBack(M.animate(el,
          { opacity: 1, transform: 'translateY(0px)' },
          { type: 'spring', stiffness: 300, damping: 28 }), el);
      }, { margin: '0px 0px -10% 0px', amount: 0.1 });
    }
  } catch (err) {
    /* Something above went wrong. Put the page back exactly as it rendered. */
    revealAll();
  }

  /* -------------------------------------------------------- 7. the failsafe */
  /* Last line of defence. If an element is on screen and still holding our
     hidden state four seconds in, something upstream failed quietly — an
     observer that never fired, a callback that threw. Show it. */
  window.setTimeout(function () {
    try {
      var vh = window.innerHeight || 0;
      for (var i = 0; i < armed.length; i++) {
        var el = armed[i];
        if (el.style.opacity !== '0') continue;
        var r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < vh) release(el);
      }
    } catch (e) {}
  }, 4000);
})();
